import { Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import axios from 'axios';
import { Button, ButtonSave } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { Breadcrumbs, VerticalLinkGroup } from 'components/navigation';
import { AlertConfirm, AlertUnsavedData, Modal, ModalSize, NotificationToaster } from 'components/notifications';
import { AuthenticatedRoute, UserAccess, UserHasAccess } from 'components/user-access';
import { useDirtyData } from 'hooks/useDirtyData';
import {
    ManualEntryTemplateActivities,
    ManualEntryTemplateDetails,
    ManualEntryTemplatePermissions,
    ManualEntryTemplateSettings
} from 'pages/employee-settings/manual-entry-templates/shared';
import { PageRestricted } from 'pages/errors/page-restricted';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { ManualEntryService } from 'services';
import { ManualEntryTemplateTemplate } from './ManualEntryTemplateTemplate';
import { useDebounce } from 'hooks/useDebounce';
import { HelperFunctions } from 'helpers/HelperFunctions';


export function ManualEntryTemplateEdit({ breadcrumbs = [] }) {
    const noAccessCode = 403;
    const validationErrorCode = 400;
    const requiredViewAction = "Employees:AddManualEntry";
    const requiredEditAction = "Employees:ManualEntryManage";
    const debounceTimeout = 1000;
    const manualEntryMultiType = "MANUALENTRYMULTI";
    const manualEntrySingleType = "MANUALENTRYSINGLE";
    const detailsTab = "details";
    const templateTab = "template";
    const accessTab = "access";

    const { templateKey, id } = useParams();
    const templateType = templateKey.toUpperCase();

    const canUserEdit = UserHasAccess(requiredEditAction);

    const [showEditModal, setShowEditModal] = useState(false);

    const [detailsValid, setDetailsValid] = useState(true);
    const [settingsValid, setSettingsValid] = useState(true);
    const [permissionsValid, setPermissionsValid] = useState(true);
    const [activitiesValid, setActivitiesValid] = useState(true);
    const [isValid, setIsValid] = useState(true);

    const [showUnauthorizedAccess, setShowUnauthorizedAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const [infringements, setInfringements] = useState([]);
    const [showInfringementModal, setShowInfringementModal] = useState(false);
    const infringementsConfirmed = useRef(false);

    const [manualEntryOptions, setManualEntryOptions] = useState({});
    const [template, setTemplate] = useState({activities: null, days: null, ruleset: null, editable: false});
    const [originalTemplate, setOriginalTemplate] = useState({activities: null, days: null, ruleset: null, editable: false});

    const isDirty = useDirtyData(originalTemplate, template, true);
    
    const debouncedActivity = useDebounce(template?.activities, debounceTimeout);
    const debouncedMultiActivity = useDebounce(template?.days, debounceTimeout);
    const currentActivity = useRef(null);
    const currentMultiActivity = useRef(null);
    const currentIsValid = useRef(false);
    const currentRuleset = useRef(null);

    let location = useLocation();
    const defaultPath = `/employee-settings/manual-entry-templates/${templateKey}/${id}/details`;
    const routes = [
        { location: `/employee-settings/manual-entry-templates/${templateKey}/${id}/details`, title: "Details" },
        { location: `/employee-settings/manual-entry-templates/${templateKey}/${id}/template`, title: "Template" },
        ...(canUserEdit &&template?.editable ? [{ location: `/employee-settings/manual-entry-templates/${templateKey}/${id}/access`, title: "Access" }] : [])
    ];

    const init = () => {
        axios
            .all([
                ManualEntryService.getOptions(),
                ManualEntryService.getTemplate(templateType, id)
            ])
            .then(
                axios.spread(function (
                    options,
                    getTemplate
                ) {
                    setManualEntryOptions(options);
                    setOriginalTemplate(deepCopy(getTemplate));
                    setTemplate(getTemplate);
                }),
                (error) => {
                    if (error.response.status === noAccessCode) {
                        setShowUnauthorizedAccess(true);
                    } else {
                        NotificationToaster.show(
                            Intent.DANGER,
                            "Could not load template, please refresh the page"
                        );
                    }
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not load template, please refresh the page"
                );
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(init, []);
    
    useEffect(() => {
        resetTemplate();
        setEditMode(false);
    }, [location]);

    const getActiveTab = useCallback(() => {
        if (location.pathname.endsWith('/details')) return detailsTab;
        if (location.pathname.endsWith('/template')) return templateTab;
        if (location.pathname.endsWith('/access')) return accessTab;
        return '';
    }, [location.pathname]);

    useEffect(() => {
        if (templateType !== manualEntryMultiType && templateType !== manualEntrySingleType) return;
        if (getActiveTab() !== templateTab) return;

        if (currentIsValid.current === isValid &&
            currentRuleset.current === template?.ruleset &&
            HelperFunctions.deepEqual(currentActivity.current, template?.activities, { strict: true }) &&
            HelperFunctions.deepEqual(currentMultiActivity.current, template?.days, { strict: true })
        ) {
            return;
        }

        currentActivity.current = template?.activities ? [...template.activities.map(item => ({ ...item }))] : undefined;
        currentMultiActivity.current = copyMultiDays(template?.days);
        currentIsValid.current = isValid;
        currentRuleset.current = template?.ruleset;

        if (templateType === manualEntryMultiType && (template?.days == null || template?.days?.length === 0)) {
            // Do not reset infringements while editing
            return;
        }

        if (templateType === manualEntrySingleType && (template?.activities == null || template?.activities?.length === 0)) {
            // Do not reset infringements while editing
            return;
        }

        checkForInfringements().catch(() => {});
    }, [debouncedActivity, debouncedMultiActivity, template?.ruleset, isValid, getActiveTab])

    useEffect(() => {
        setIsValid(detailsValid && settingsValid && permissionsValid && activitiesValid);
    }, [detailsValid, settingsValid, permissionsValid, activitiesValid]);

    function resetTemplate() {
        setTemplate(deepCopy(originalTemplate));
    }

    function onSaveTemplateClick() {
        setSaving(true);
        
        const activeTab = getActiveTab();
        if ((activeTab === templateTab || activeTab === detailsTab) && (templateType === manualEntryMultiType || templateType === manualEntrySingleType) && !infringementsConfirmed.current) {
            checkForInfringements().then((response) => {
                if (response?.length > 0) {
                    setShowInfringementModal(true);
                } else {
                    saveTemplate();
                }
            }, () => {
                NotificationToaster.show(Intent.DANGER, "Could not check for infringements, please try again later");
                setSaving(false);
                setShowEditModal(false);
            });
        } else {
            saveTemplate();
        }
    }
    
    function checkForInfringements() {
        if (!isValid) {
            setInfringements([]);
            return Promise.resolve([]);
        }

        const requestDays = templateType === manualEntryMultiType ? template?.days : [{
            dayNumber: 1,
            shifts: [
                [
                    ...template?.activities ?? []
                ]
            ]
        }];

        const request = {
            ruleset: template?.ruleset,
            Days: requestDays
        };

        return new Promise((resolve, reject) => {

            ManualEntryService.checkForInfringments(request).then((response) => {
                setInfringements(response);
                resolve(response);
            }, () => {
                NotificationToaster.show(Intent.DANGER, "Could not check for infringements, please try again later");
                reject();
            });

        });

    }

    function saveTemplate() {
        const templateCopy = deepCopy(template, false);
        ManualEntryService.updateTemplate(templateType, id, templateCopy)
            .then(() => {
                setOriginalTemplate(deepCopy(template));
                NotificationToaster.show(Intent.SUCCESS, "Your template was updated successfully");
                setEditMode(false);
            }, (error) => {
                if (error.response.status === validationErrorCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, "Could not update template, please try again");
                }
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not update template, please try again");
            })
            .finally(() => {
                setSaving(false);
                setShowEditModal(false);
            });
    }

    function onTemplateUpdate(updates) {
        setTemplate((prev) => {
            return {
                ...prev,
                ...updates
            };
        })
    }

    function onInfringementModalCancel() {
        setShowInfringementModal(false);
        setSaving(false);
    }

    function onInfringementModalConfirm() {
        setShowInfringementModal(false);
        saveTemplate();
        infringementsConfirmed.current = true;
    }

    function onCloseEditModal() {
        setShowEditModal(false);
        resetTemplate();
    }

    function copyShifts(shifts) {
        if (!Array.isArray(shifts)) return [];
        return shifts.map(shift => Array.isArray(shift) ? shift.map(item => ({ ...item })) : []);
    }

    function copyMultiDays(days) {
        if (!Array.isArray(days)) return undefined;
        return days.map(day => day ? { ...day, shifts: copyShifts(day.shifts) } : null);
    }

    function deepCopy(templateToCopy, withUsers = true) {
        return {
            ...templateToCopy,
            ...(templateToCopy.activities ? { activities: templateToCopy.activities.map(a => ({ ...a })) } : {}),
            ...(templateToCopy.days ? { days: copyMultiDays(templateToCopy.days) } : {}),
            ...(templateToCopy.dayNumbers ? { dayNumbers: [...templateToCopy.dayNumbers] } : {}),
            ...(templateToCopy.access ? { access: [...templateToCopy.access] } : {}),
            ...(templateToCopy.users ? { users: withUsers ? templateToCopy.users.map(u => ({ ...u })) : null } : {})
        }
    }

    return (
        <UserAccess perform={requiredViewAction}
            yes={() => (
                <ShowHide
                    evaluator={!showUnauthorizedAccess}
                    show={
                        <Fragment>
                            <div className="row">
                                <Breadcrumbs items={breadcrumbs} />
                                <h1>Manual Entry Template Settings</h1>
                            </div>
                            
                            <div className="layout-cols">
                                <div className="col side-col">
                                    <div className="row">
                                        <VerticalLinkGroup links={routes} exactRouteMatch={false} />
                                    </div>
                                </div>
                                <div className="col content-col">
                                    <Routes>
                                        <Route path="details" element={<AuthenticatedRoute action={requiredViewAction}>
                                            <div>
                                                <h2>Details</h2>
                                                <ShowHide
                                                    evaluator={canUserEdit && template?.editable}
                                                    show={
                                                        <div className="spacer-bottom">
                                                            <ShowHide
                                                                evaluator={editMode}
                                                                show={<ButtonSave id="save-template-details-button" loading={loading} onClick={onSaveTemplateClick} disabled={saving} simpleDisabled={!isDirty || !isValid} />}
                                                                hide={<Button id="edit-template-details-button" intent={Intent.PRIMARY} loading={loading} onClick={() => setEditMode(true)} text='Edit Details' icon={IconNames.EDIT} />}
                                                            />
                                                        </div>
                                                    }
                                                />
                                                <ManualEntryTemplateDetails
                                                    template={template}
                                                    loading={loading}
                                                    saving={saving}
                                                    onTemplateUpdate={onTemplateUpdate}
                                                    onValidChange={setDetailsValid}
                                                    readOnly={!editMode}
                                                />
                                                <ManualEntryTemplateSettings
                                                    template={template}
                                                    templateType={templateType}
                                                    options={manualEntryOptions}
                                                    loading={loading}
                                                    saving={saving}
                                                    onValidChange={setSettingsValid}
                                                    onTemplateTypeChange={() => {}}
                                                    onTemplateUpdate={onTemplateUpdate}
                                                    withSelectType={false}
                                                    readOnly={!editMode}
                                                />
                                            </div>
                                        </AuthenticatedRoute>} />
                                        <Route path="template" element={<AuthenticatedRoute action={requiredViewAction}>
                                            <div>
                                                <h2>Template</h2>
                                                <ShowHide
                                                    evaluator={canUserEdit && template?.editable}
                                                    show={
                                                        <div className='spacer-bottom'>
                                                            <Button id="edit-activities-button" intent={Intent.PRIMARY} onClick={() => setShowEditModal(true)} text='EDIT TEMPLATE' icon={IconNames.Edit} />
                                                        </div>
                                                    }
                                                />
                                                <ManualEntryTemplateTemplate
                                                    templateType={templateType}
                                                    template={template}
                                                    options={manualEntryOptions}
                                                    infringements={infringements}
                                                />
                                            </div>
                                        </AuthenticatedRoute>} />
                                        {!loading && <Route path="access" element={<AuthenticatedRoute action={requiredEditAction}>
                                            <ShowHide
                                                evaluator={canUserEdit && template?.editable}
                                                show={
                                                    <div>
                                                        <h2>Access</h2>
                                                        <div className="spacer-bottom">
                                                            <ShowHide
                                                                evaluator={editMode}
                                                                show={<ButtonSave id="save-template-access-button" loading={loading} onClick={onSaveTemplateClick} disabled={saving} simpleDisabled={!isDirty || !isValid} />}
                                                                hide={<Button id="edit-template-access-button" intent={Intent.PRIMARY} loading={loading} onClick={() => setEditMode(true)} text='Edit Access' icon={IconNames.EDIT} />}
                                                            />
                                                        </div>
                                                        <ManualEntryTemplatePermissions
                                                            template={template}
                                                            options={manualEntryOptions}
                                                            loading={loading}
                                                            saving={saving}
                                                            onTemplateUpdate={onTemplateUpdate}
                                                            onValidChange={setPermissionsValid}
                                                            readOnly={!editMode}
                                                        />
                                                    </div>
                                                }
                                                hide={<PageRestricted />}
                                            />
                                        </AuthenticatedRoute>} />}
                                        <Route path="" element={<Navigate to={defaultPath} replace />} />
                                    </Routes>
                                </div>
                            </div>

                            <div>
                                <AlertUnsavedData
                                    isDirty={isDirty}
                                    preserveSearch={true}
                                />
                            </div>
                            
                            <AlertConfirm
                                isOpen={showInfringementModal}
                                title='Template Contains Infringements'
                                confirmButtonIntent='primary'
                                onCancel={onInfringementModalCancel}
                                onConfirm={onInfringementModalConfirm}
                            >
                                <p>Are you sure you want to continue, if you apply this template to employees they will receive infringement(s) as a result.</p>
                            </AlertConfirm>
            
                            <Modal
                                isOpen={showEditModal}
                                onClose={onCloseEditModal}
                                clickOutsideClose={!saving}
                                showCloseButton={false}
                                updateStateOnClose={false}
                                overflowAuto
                                className='edit-activities-modal'
                                size={templateType === manualEntryMultiType ? ModalSize.LARGE : ModalSize.MEDIUM}
                                footer={
                                    <div className="button-row pull-right">
                                        <Button text="CANCEL" intent={Intent.NONE} onClick={onCloseEditModal} disabled={saving} className="cancel-button" />
                                        <Button text="SAVE AND CLOSE" loading={saving} intent={Intent.PRIMARY} onClick={onSaveTemplateClick} disabled={!isDirty || !isValid} />
                                    </div>
                                }
                            >
                                <ManualEntryTemplateActivities
                                    templateType={templateType}
                                    template={template}
                                    infringements={infringements}
                                    onActivitiesValidChange={setActivitiesValid}
                                    onTemplateUpdate={onTemplateUpdate}
                                    editMode
                                />
                            </Modal>
                        </Fragment>
                    }
                    hide={<PageRestricted />}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />);
}